"use client";

import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Api } from "@/app/api";
import type {
	AdminUpdateCompanyReviewPayload,
	CompanyReviewAdminCollection,
	CompanyReviewAdminDoc,
	CompanyReviewCollection,
	CreateCompanyReviewPayload,
	CompanyReviewHeroSummary,
} from "@/types/companyReview";
import { compareApprovedReviewsByReviewDate } from "@/lib/utils/company-review-sort";

const PUBLIC_REVIEWS_PAGE_SIZE = 200;

export const companyReviewInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getPublicCompanyReviews: builder.query<
			CompanyReviewCollection,
			{ page?: number; limit?: number; hasVideo?: boolean }
		>({
			query: ({ page = 1, limit = 100, hasVideo } = {}) => ({
				url: "/company-reviews/public",
				params: {
					page,
					limit,
					...(hasVideo !== undefined ? { hasVideo: String(hasVideo) } : {}),
				},
			}),
			providesTags: [{ type: "company-reviews", id: "PUBLIC" }],
		}),

		getCompanyReviewsHeroSummary: builder.query<CompanyReviewHeroSummary, void>({
			query: () => ({
				url: "/company-reviews/public/hero-summary",
			}),
			providesTags: [{ type: "company-reviews", id: "HERO_SUMMARY" }],
		}),

		/** All approved public reviews (paginates until complete). Use on /reviews only — can be large. */
		getAllPublicCompanyReviews: builder.query<
			CompanyReviewCollection,
			{ hasVideo?: boolean } | void
		>({
			async queryFn(arg, _api, _extraOptions, baseQuery) {
				let page = 1;
				const allDocs: CompanyReviewCollection["docs"] = [];
				let totalDocs = 0;
				let totalPages = 1;
				let averageRating: number | undefined;
				const hasVideo = arg && "hasVideo" in arg ? arg.hasVideo : undefined;

				for (;;) {
					const result = await baseQuery({
						url: "/company-reviews/public",
						params: {
							page,
							limit: PUBLIC_REVIEWS_PAGE_SIZE,
							...(hasVideo !== undefined ? { hasVideo: String(hasVideo) } : {}),
						},
					});
					if (result.error) {
						return { error: result.error as FetchBaseQueryError };
					}
					const data = result.data as CompanyReviewCollection;
					allDocs.push(...(data.docs ?? []));
					totalDocs = data.totalDocs;
					totalPages = data.totalPages;
					if (data.averageRating != null) {
						averageRating = data.averageRating;
					}
					if (!data.hasNextPage || page >= totalPages) break;
					page += 1;
					if (page > 80) break;
				}

				allDocs.sort(compareApprovedReviewsByReviewDate);

				return {
					data: {
						docs: allDocs,
						totalDocs: totalDocs || allDocs.length,
						totalPages: 1,
						page: 1,
						limit: allDocs.length,
						pagingCounter: allDocs.length ? 1 : 0,
						prevPage: null,
						nextPage: null,
						hasPrevPage: false,
						hasNextPage: false,
						...(averageRating != null ? { averageRating } : {}),
					},
				};
			},
			providesTags: [{ type: "company-reviews", id: "PUBLIC" }],
		}),

		createCompanyReview: builder.mutation<unknown, CreateCompanyReviewPayload>({
			query: (body) => ({
				url: "/company-reviews",
				method: "POST",
				body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: [
				{ type: "company-reviews", id: "PUBLIC" },
				{ type: "company-reviews", id: "ADMIN_LIST" },
			],
		}),

		getAdminCompanyReviews: builder.query<
			CompanyReviewAdminCollection,
			{
				page?: number;
				limit?: number;
				status?: string;
				hasVideo?: boolean;
				sortBy?: "reviewDate" | "submittedAt";
				search?: string;
			}
		>({
			query: ({
				page = 1,
				limit = 100,
				status = "PENDING",
				hasVideo,
				sortBy = "reviewDate",
				search,
			} = {}) => ({
				url: "/company-reviews/moderation",
				params: {
					page,
					limit,
					status,
					sortBy,
					...(hasVideo !== undefined ? { hasVideo: String(hasVideo) } : {}),
					...(search?.trim() ? { search: search.trim().slice(0, 200) } : {}),
				},
			}),
			providesTags: [{ type: "company-reviews", id: "ADMIN_LIST" }],
		}),

		updateAdminCompanyReview: builder.mutation<
			CompanyReviewAdminDoc,
			AdminUpdateCompanyReviewPayload
		>({
			query: ({ id, ...body }) => ({
				url: `/company-reviews/moderation/${id}`,
				method: "PATCH",
				body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: [
				{ type: "company-reviews", id: "ADMIN_LIST" },
				{ type: "company-reviews", id: "PUBLIC" },
			],
		}),

		deleteAdminCompanyReview: builder.mutation<{ ok: boolean }, string>({
			query: (id) => ({
				url: `/company-reviews/moderation/${id}`,
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: [
				{ type: "company-reviews", id: "ADMIN_LIST" },
				{ type: "company-reviews", id: "PUBLIC" },
			],
		}),
	}),
});

export const {
	useGetPublicCompanyReviewsQuery,
	useGetCompanyReviewsHeroSummaryQuery,
	useGetAllPublicCompanyReviewsQuery,
	useCreateCompanyReviewMutation,
	useGetAdminCompanyReviewsQuery,
	useUpdateAdminCompanyReviewMutation,
	useDeleteAdminCompanyReviewMutation,
} = companyReviewInjection;
